/**
 * WHICH LANE A CONVERSATION IS ON — the one client-side owner of that fact, and it defers to the SERVER.
 *
 * Same philosophy as `../protocol/negotiation.ts`: nothing here consults a build flag, storage or the
 * URL. The server advertises; the client believes it.
 *
 *   · New conversations: `GET /api/sessions/capabilities` (R0). A 404 (a server that predates the
 *     read model) or a malformed 200 → LEGACY, i.e. exactly today's behaviour. A 5xx / network error /
 *     429 is NOT evidence of an old server: it is retried with backoff and, if it persists, reported as
 *     CONNECTING (never cached, never legacy). Only `200 {read_model >= 1, lane_v2: true}` makes a new
 *     chat lane v2.
 *   · Existing conversations (history / URL / persisted): `GET /api/sessions/{id}` (R1). 200 → v2;
 *     anything else → legacy. R1 is only asked when R0 said the read model exists, so on an old
 *     server the legacy path makes no request it did not make before except the one R0 probe.
 *
 * WHY THE ORDER MATTERS. Minting a stream ticket (or submitting a turn) PINS a session to lane v2 and
 * the old routes 409 it forever. So the ticket/turn routes are only ever called for a session this
 * module resolved to v2. The resolution itself is read-only on the server.
 *
 * The rules are pure functions over already-fetched results (testable in the node suite); the cache
 * at the bottom is the only state: one capabilities answer per org, dropped on an org switch.
 */
import { registerOrgScopedReset } from "@/lib/org-scope";
import { getCapabilities, getSession, isLaneV2Error } from "./api";
import type { LaneV2Capabilities, LaneV2Session } from "./types";

export type Lane = "legacy" | "v2";

/** What R0 told us, normalised. `readModel` gates R1 probes; `laneV2` gates NEW conversations. */
export interface LaneCapabilities {
  readModel: boolean;
  laneV2: boolean;
  deployMode: "off" | "check_only" | "apply" | string | null;
  /** R0 `turn_answers_ask` — gates the `respond_then_turn` route in `askRouting.ts`. */
  turnAnswersAsk: boolean;
  /** R0 `offline` — the operator kill switch, read live, never cached beyond this per-org R0 answer. */
  offline: boolean;
  /** R0 `session_list` — gates `GET /api/sessions` (R6) + `POST /{id}/close` in the history surfaces. */
  sessionList: boolean;
  /** R0 `turn_attachments` — gates sending `TurnIn.attachments`. */
  turnAttachments: boolean;
  /** R0 `turn_ide_state` — gates sending `TurnIn.ide_state`. */
  turnIdeState: boolean;
  /** R0 `branch_save` — gates `POST .../tasks/{t}/save`. */
  branchSave: boolean;
  /** R0 `live_frames` — the session stream may carry transient `turn_activity` frames. */
  liveFrames: boolean;
  /** R0 could not be reached (5xx / network / 429, after the backoff). Not legacy and not cached: the caller shows "Connecting…" and asks again. */
  connecting: boolean;
  /** R0 answered 401/403: signed out or no access. Not an old server and not transient: no polling, the caller says so. */
  unauthorized: boolean;
}

/** The legacy answer: no read model, no lane v2. What every failure resolves to. */
export const LEGACY_CAPABILITIES: LaneCapabilities = Object.freeze({
  readModel: false,
  laneV2: false,
  deployMode: null,
  turnAnswersAsk: false,
  offline: false,
  sessionList: false,
  turnAttachments: false,
  turnIdeState: false,
  branchSave: false,
  liveFrames: false,
  connecting: false,
  unauthorized: false,
});

/** R0 could not be reached: legacy-shaped (no feature is on) but flagged so nothing treats it as an old server. */
export const CONNECTING_CAPABILITIES: LaneCapabilities = Object.freeze({ ...LEGACY_CAPABILITIES, connecting: true });

/** R0 said 401/403: its own state (signed out / no access), never cached and never polled. */
export const UNAUTHORIZED_CAPABILITIES: LaneCapabilities = Object.freeze({ ...LEGACY_CAPABILITIES, unauthorized: true });

/** Outcome of an R0/R1 call, as the pure rules see it. */
export type Probe<T> = { ok: true; value: T } | { ok: false; status: number | null };

/** Only a 404 says "this server has no read model"; every other failure (5xx, network, 429, 401...) is transient. */
export function isDefinitiveLegacyProbe(probe: Probe<unknown>): boolean {
  return !probe.ok && probe.status === 404;
}

/** R0 → capabilities. A 404 or malformed body is LEGACY; any other failure is CONNECTING. */
export function capabilitiesFromProbe(probe: Probe<Partial<LaneV2Capabilities> | null | undefined>): LaneCapabilities {
  if (!probe.ok && (probe.status === 401 || probe.status === 403)) return UNAUTHORIZED_CAPABILITIES;
  if (!probe.ok && !isDefinitiveLegacyProbe(probe)) return CONNECTING_CAPABILITIES;
  if (!probe.ok || !probe.value || typeof probe.value !== "object") return LEGACY_CAPABILITIES;
  const v = probe.value;
  const readModel = typeof v.read_model === "number" && v.read_model >= 1;
  if (!readModel) return LEGACY_CAPABILITIES;
  return {
    readModel: true,
    laneV2: v.lane_v2 === true,
    deployMode: typeof v.package_deploy_mode === "string" ? v.package_deploy_mode : null,
    turnAnswersAsk: v.turn_answers_ask === true,
    offline: v.offline === true,
    sessionList: v.session_list === true,
    turnAttachments: v.turn_attachments === true,
    turnIdeState: v.turn_ide_state === true,
    branchSave: v.branch_save === true,
    liveFrames: v.live_frames === true,
    connecting: false,
    unauthorized: false,
  };
}

/** The lane for a brand-new conversation. */
export function laneForNewConversation(caps: LaneCapabilities): Lane {
  return caps.readModel && caps.laneV2 ? "v2" : "legacy";
}

/**
 * The lane for an EXISTING conversation, from R1. Only a 200 whose body says `lane: "v2"` (or omits
 * `lane` on a 200 — R1 only answers 200 for v2 sessions) is v2; a 404 or any other failure is legacy.
 */
export function laneForExistingSession(caps: LaneCapabilities, probe: Probe<Partial<LaneV2Session> | null> | null): Lane {
  if (!caps.readModel || !probe || !probe.ok || !probe.value) return "legacy";
  const lane = probe.value.lane;
  return lane === undefined || lane === "v2" ? "v2" : "legacy";
}

/** Turn a promise into a `Probe` — never throws. */
export async function probe<T>(p: Promise<T>): Promise<Probe<T>> {
  try {
    return { ok: true, value: await p };
  } catch (err) {
    return { ok: false, status: isLaneV2Error(err) ? err.status : null };
  }
}

// ── applying the lane to the ACTIVE conversation (what `useLaneV2Route` runs) ──────────────────────

/** What the route knows when it decides. */
export interface RouteInputs {
  /** The active conversation (`sessionIdentity.activeConversationId`), or null for a new chat. */
  active: string | null;
  /** A `?session=` deep link (absent when `?run=` is present — that is a legacy run link). */
  querySessionId: string | null;
  /** The lane-v2 store's session. */
  v2: { sessionId: string | null; draft: boolean; streaming: boolean };
  /** True when this route already attached `target` (see `routeAction`). */
  attached: (sessionId: string) => boolean;
}

export type RouteAction =
  /** R1 the session; v2 → attach it (adopting it as the active conversation when `adopt`); else leave v2. */
  | { kind: "probe"; sessionId: string; adopt: boolean }
  /** A new conversation: R0 → a v2 draft if the server offers lane v2, else legacy. */
  | { kind: "new-conversation" }
  /** Already right. */
  | { kind: "none" };

/**
 * The one rule for which conversation the route resolves, and how. Pure.
 *
 *   1. a `?session=` link that is not already active → probe it (and adopt it if v2);
 *   2. otherwise the active conversation, or — when there is none — the v2 store's PINNED session
 *      (restored on reload before the legacy store knows about it);
 *   3. nothing at all → a new conversation (unless a draft is already open);
 *   4. a target this route already attached, or one this tab just pinned (its stream is running) →
 *      nothing to do; anything else → probe.
 */
export function routeAction(input: RouteInputs): RouteAction {
  if (input.querySessionId && input.querySessionId !== input.active) {
    return { kind: "probe", sessionId: input.querySessionId, adopt: true };
  }
  const pinned = input.v2.sessionId && !input.v2.draft ? input.v2.sessionId : null;
  const target = input.active ?? pinned;
  if (!target) return input.v2.sessionId && input.v2.draft ? { kind: "none" } : { kind: "new-conversation" };
  if (input.v2.sessionId === target && input.attached(target)) return { kind: "none" };
  if (input.v2.sessionId === target && !input.v2.draft && input.v2.streaming) return { kind: "none" };
  return { kind: "probe", sessionId: target, adopt: false };
}

/**
 * Does the route render the lane-v2 view right now? Only for the conversation the v2 store holds: a
 * draft while no other conversation is active, or the pinned session when it IS the active one (or
 * nothing else is). Anything else renders legacy immediately — before any probe answers — so opening
 * a legacy chat from history never flashes the v2 view.
 */
export function showsLaneV2(active: string | null, v2: { sessionId: string | null; draft: boolean }): boolean {
  if (!v2.sessionId) return false;
  if (v2.draft) return !active;
  return !active || v2.sessionId === active;
}

/**
 * Which transport the ONE composer sends on. `blocked`: the conversation's lane is still being resolved (or its
 * lookup failed) — sending would take the old lane, which 409s a v2 session. Pure.
 */
export function sendPathFor(input: { laneV2Active: boolean; resolving: boolean; resolveError: string | null }): "v2" | "blocked" {
  if (input.resolving || input.resolveError || !input.laneV2Active) return "blocked";
  return "v2";
}

/** What the route reports about the active conversation's lane resolution. Pure. */
export function routeSurfaceFor(input: {
  active: string | null;
  shown: boolean;
  v2Sourced: boolean;
  resolution: { sessionId: string; status: "resolving" } | { sessionId: string; status: "error"; message: string } | null;
}): { resolving: boolean; resolveError: string | null } {
  const { active, shown, v2Sourced, resolution } = input;
  const here = !shown && resolution && resolution.sessionId === active ? resolution : null;
  const sourcedPending = !shown && !!active && v2Sourced && here?.status !== "error";
  return {
    resolving: here?.status === "resolving" || sourcedPending,
    resolveError: here?.status === "error" ? here.message : null,
  };
}

/**
 * What to do with a persisted conversation id whose lane probe came back NOT v2 (a legacy session from before the
 * run lane was removed). Only a `?session=` deep link is kept on screen (it shows the link error); a persisted id
 * has nothing to show and would leave an empty chat nothing can be sent from, so it starts a fresh V2 chat.
 */
export function onNonV2Resolution(input: { lane: "v2" | "legacy"; fromDeepLink: boolean }): "start-new" | "keep" | "none" {
  if (input.lane === "v2") return "none";
  return input.fromDeepLink ? "keep" : "start-new";
}

/** Shown when R0 answers for a NEW chat but lane v2 is not offered: the composer is closed, so say why. */
export const LANE_V2_UNAVAILABLE_MESSAGE =
  "Weave sessions are not available for your organisation right now, so a new chat cannot be started. Try again shortly.";

// ── the cache: one R0 answer per org ─────────────────────────────────────────────────────────────

const capsCache = new Map<string, Promise<LaneCapabilities>>();

/** Backoff between R0 attempts on a transient failure (3 attempts in all). Tests shrink it via `setCapabilitiesBackoff`. */
let capsBackoffMs: readonly number[] = [400, 1200];

export function setCapabilitiesBackoff(delays: readonly number[]): void {
  capsBackoffMs = delays;
}

/** Orgs whose last R0 answer was CONNECTING; the first definitive answer after one announces recovery to subscribers. */
const wasConnecting = new Set<string>();
const recoveryListeners = new Set<() => void>();

/** Subscribe to "R0 answered after being unreachable" (the history list refetches then). Returns the unsubscribe. */
export function subscribeCapabilitiesRecovered(fn: () => void): () => void {
  recoveryListeners.add(fn);
  return () => recoveryListeners.delete(fn);
}

/** Delay before the route asks R0 again after the `attempt`th consecutive miss: 5 s doubling, capped at 60 s. */
export function nextReprobeDelayMs(attempt: number): number {
  return Math.min(5000 * 2 ** Math.max(0, attempt), 60_000);
}

async function probeCapabilitiesWithBackoff(): Promise<LaneCapabilities> {
  for (let attempt = 0; ; attempt++) {
    const p = await probe(getCapabilities());
    const caps = capabilitiesFromProbe(p);
    if (!caps.connecting || attempt >= capsBackoffMs.length) return caps;
    await new Promise((resolve) => setTimeout(resolve, capsBackoffMs[attempt]));
  }
}

/**
 * Capabilities for the org, fetched once per org per page (a server does not change its read model
 * mid-session in any way the GUI must follow live; a deploy is picked up on reload). Only a DEFINITIVE
 * answer is cached: a 200, or a 404 (old server → LEGACY, so the legacy path never grows extra traffic).
 * A transient failure (5xx, network, 429) is retried with backoff and, if it persists, resolves to
 * CONNECTING and is evicted so the next call asks again — it must never pin the tab to the old lane.
 */
export function resolveCapabilities(orgId: string | null): Promise<LaneCapabilities> {
  const key = orgId ?? "";
  const hit = capsCache.get(key);
  if (hit) return hit;
  const pending = probeCapabilitiesWithBackoff().then((caps) => {
    // Only a definitive answer is cached: CONNECTING and UNAUTHORIZED are asked again next time (a re-login must work).
    if ((caps.connecting || caps.unauthorized) && capsCache.get(key) === pending) capsCache.delete(key);
    if (caps.connecting) wasConnecting.add(key);
    else if (!caps.unauthorized && wasConnecting.delete(key)) recoveryListeners.forEach((fn) => fn());
    return caps;
  });
  capsCache.set(key, pending);
  return pending;
}

/** R1 for one session, only when R0 said the read model exists. Returns the lane and the R1 body. */
export async function resolveSessionLane(
  orgId: string | null,
  sessionId: string,
): Promise<{ lane: Lane; session: LaneV2Session | null; caps: LaneCapabilities; failure: { status: number | null } | null }> {
  const caps = await resolveCapabilities(orgId);
  if (!caps.readModel) return { lane: "legacy", session: null, caps, failure: null };
  const r1 = await probe(getSession(sessionId));
  const lane = laneForExistingSession(caps, r1);
  // `failure`: the R1 lookup itself failed (404, 5xx, network). `lane` is still "legacy" then (unchanged rule); a caller
  // that KNOWS the session is v2 (a history row's source) shows an error + retry instead of acting on that.
  return { lane, session: lane === "v2" && r1.ok ? r1.value : null, caps, failure: r1.ok ? null : { status: r1.status } };
}

/**
 * Forget one org's cached R0 answer so the next `resolveCapabilities` probes again. Used by a retry and by an attach
 * of a session the server is known to have pinned to v2 when the cached answer is LEGACY (a probe that failed once
 * must not strip the chip, Save and the deploy mode from a v2 conversation for the life of the page).
 */
export function invalidateCapabilities(orgId: string | null): void {
  capsCache.delete(orgId ?? "");
}

/** Drop the cache (org switch, tests). */
export function clearLaneCache(): void {
  capsCache.clear();
}

/**
 * Overwrite the cached answer for one org with a freshly-probed one (review fix — offline re-probe).
 * Without this, `startDraft`/`attach` reading `resolveCapabilities` after the operator flips `offline`
 * back on kept getting the STALE (offline-at-attach-time) answer for the rest of the page's life, since
 * `resolveCapabilities` only ever probes once per org and remembers whatever it first saw. The store's
 * own re-probe loop (`laneV2SessionStore.ts`'s `markOffline`) calls this after every R0 answer — online
 * or still offline — so the next `resolveCapabilities` call (a fresh tab, a new draft) reflects it.
 */
export function setCachedCapabilities(orgId: string | null, caps: LaneCapabilities): void {
  capsCache.set(orgId ?? "", Promise.resolve(caps));
}

registerOrgScopedReset("laneV2Capabilities", clearLaneCache);

// ── the session id in the URL (two tabs must not fight over the one persisted id) ───────────────────

/**
 * A `?session=` value is a one-shot deep link: the route probes it ONCE (`consumed` then holds it) and from then on the URL
 * is only ever WRITTEN from the active conversation (`sessionParamAction`). Without this, every differing URL session was
 * re-probed on every render, so "New chat" and switching from History bounced back to the link's session. Pure.
 */
export function pendingQuerySession(querySessionId: string | null, consumed: string | null): string | null {
  return querySessionId && querySessionId !== consumed ? querySessionId : null;
}

/**
 * What to do to `?session=` so the URL follows the conversation on screen. ONE owner (the route); the legacy store no longer
 * strips this param. Pure.
 *   · an unconsumed deep link is left alone (the URL wins on reload; the persisted id only serves a bare /weave);
 *   · the conversation on screen is a v2 session THIS tab attached → write its id (replace, not push);
 *   · anything else (a new chat/draft, a legacy conversation, a consumed link for another session) → remove a lingering param.
 */
export function sessionParamAction(input: {
  querySessionId: string | null;
  consumed: string | null;
  active: string | null;
  v2: { sessionId: string | null; draft: boolean };
  attachedHere: boolean;
}): { kind: "set"; sessionId: string } | { kind: "remove" } | null {
  const { querySessionId, consumed, active, v2, attachedHere } = input;
  if (pendingQuerySession(querySessionId, consumed)) return null;
  const pinned = v2.sessionId && !v2.draft ? v2.sessionId : null;
  const shown = active ?? pinned;
  if (shown && pinned === shown && attachedHere) return querySessionId === shown ? null : { kind: "set", sessionId: shown };
  if (shown && querySessionId === shown) return null; // never strip the param that names the conversation on screen
  return querySessionId ? { kind: "remove" } : null;
}

/**
 * The consumed link after this tab's own URL activity. The route's OWN `set` write, and a link that already names the
 * conversation on screen, are consumed: they are not deep links, so a later New chat must not "open" them. Pure.
 */
export function consumedAfter(input: {
  consumed: string | null;
  querySessionId: string | null;
  active: string | null;
  write: { kind: "set"; sessionId: string } | { kind: "remove" } | null;
}): string | null {
  if (input.write?.kind === "set") return input.write.sessionId;
  if (input.querySessionId && input.querySessionId === input.active) return input.querySessionId;
  return input.consumed;
}

/**
 * What a route branch does with an R0 answer BEFORE acting on it (new chat, probe of an existing session, and a history
 * row already known to be v2 all go through this one rule): wait while R0 is unreachable, stop on 401/403, else go. Pure.
 */
export function capsGate(caps: { connecting: boolean; unauthorized: boolean }): "wait" | "unauthorized" | "go" {
  if (caps.connecting) return "wait";
  return caps.unauthorized ? "unauthorized" : "go";
}

/** A deep-linked session the server does not know for this org/user: one indistinguishable answer, shown (not a crash, not a bounce). */
export function linkErrorFor(failure: { status: number | null } | null): string | null {
  if (!failure) return null;
  return failure.status === 404
    ? "This conversation could not be found. It may belong to another organization."
    : "Could not open this conversation. Check your connection and try again.";
}
